import React, { useState, useEffect } from 'react'
import { ButtonBasic, Sceleton } from '@lk-gtcom/ecomlab-components'
import './TariffPlanV2.scss'
import customHeaders, { getSpace } from '../../../common/headers';
import useGeneralStore from '../../../store/general';
import { useResponseHandler } from '../../../common/auth';
import { usePushNotifications } from '../../../common/pushNotification';

const CheckIcon = () => (
  <svg className="tariff-card__feature-icon" viewBox="0 0 15 15" fill="none">
    <path d="M3 7.5l3 3 6-6"
      stroke="currentColor" strokeWidth="1.8"
      strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

// Ответ /tariff/available — чистый массив тарифов.
// На случай сбоя/неожиданной формы ответа — всегда приводим к массиву,
// чтобы .map() ниже не уронил рендер.
const parseTariffResponse = (json) => {
  if (Array.isArray(json)) return json
  console.warn('Неожиданный формат ответа /tariff/available:', json)
  return []
}

// Безопасный парсинг цены. Бэк отдаёт price строкой, напр. "2990.00"
const parsePrice = (priceStr) => {
  const num = parseFloat(priceStr)
  return Number.isNaN(num) ? null : num
}

const TariffCard = ({ tariff, onCtaClick }) => {

  const priceNum = parsePrice(tariff.price)
  const isFree = priceNum === 0
  const hasPrice = priceNum !== null

  const priceText = isFree
    ? `0 ${tariff.currency}`
    : hasPrice
      ? `${Math.round(priceNum).toLocaleString('ru-RU')} ${tariff.currency}`
      : 'Индивидуально'

  const cardCls = ['tariff-card', tariff.is_recommended ? 'tariff-card_popular' : '']
    .filter(Boolean).join(' ')

  const priceCls = [
    'tariff-card__price',
    isFree ? 'tariff-card__price_free' : '',
    !hasPrice ? 'tariff-card__price_custom' : '',
  ].filter(Boolean).join(' ')

  const periodCls = [
    'tariff-card__price-period',
    isFree ? 'tariff-card__price-period_accent' : '',
  ].filter(Boolean).join(' ')

  return (
    <article className={cardCls}>
      {tariff.is_recommended && (
        <div className="tariff-card__badge">
          Рекомендуемый
        </div>
      )}

      <div className="tariff-card__icon">
        <img src={tariff.icon} alt={tariff.portal_name} />
      </div>

      <h3 className="tariff-card__name">{tariff.portal_name}</h3>

      <p className="tariff-card__gmv">{tariff.description}</p>
      <p className="tariff-card__gmv-label">GMV за предыдущий месяц</p>

      <p className={priceCls}>{priceText}</p>
      <p className={periodCls}>{isFree ? 'Бесплатно' : 'в месяц'}</p>

      <ul className="tariff-card__features">
        {(tariff.features || []).map((feat) => (
          <li key={feat} className="tariff-card__feature">
            <CheckIcon />
            {feat}
          </li>
        ))}
      </ul>

      <ButtonBasic
        green
        width='100%'
        disabled={!tariff.is_available}
        onClick={() => onCtaClick?.(tariff?.id)}
        text={"Выбрать тариф"}
      />
    </article>
  )
}


const TariffPlanV2 = ({
  setIsModalBalance,
  setAddedSum,
  child,
  fetchGetActivateTariff = () => { },
  title = 'Тарифы по обороту',
  description = 'Платите только за результат. Выбирайте тариф в зависимости от оборота вашего магазина за предыдущий месяц.',
  onCtaClick = () => { window?.open(`https://lk.ecomru.ru/settings/tariff/tariffs`, '_blunk') },
}) => {

  const { handleTableResponse, handleRequestError } = useResponseHandler();
  const { addErrorPush, addSuccessPush } = usePushNotifications();
  const selectedSpace = useGeneralStore((state) => state.selectedSpace);
  const headers = { ...customHeaders, ...getSpace(selectedSpace) };

  const [modelsTariff, setModelsTariff] = useState([])
  const [currentTariff, setCurrentTariff] = useState(null)
  const [loading, setLoading] = useState(false)

  // Получение моделей тарифов
  const fetchGetModelsTariff = (period) => {
    setLoading(true)
    const url = `https://apps0.ecomru.ru:4453/api/v1/tariff/available?portal_id=4&period=${period}`;

    fetch(url, { method: 'GET', headers, })
      .then(handleTableResponse)
      .then((json) => setModelsTariff(parseTariffResponse(json)))
      .catch(handleRequestError)
      .finally(() => setLoading(false));
  };

  // Активировать тариф
  const fetchActivateTariff = (id) => {

    const url = `https://apps0.ecomru.ru:4453/api/v1/tariff/activate`;

    const body = JSON.stringify({ id });

    fetch(url, { method: 'POST', headers, body })
      .then(response => {
        if (response.status === 400) {
          // Обработка ошибки 400

          return response.json().then(data => {
            addErrorPush(data?.detail)
            throw { status: 400, data };
          });
        }
        return handleTableResponse(response);
      })
      .then((json) => {
        if (json && !json?.is_paid) {
          addErrorPush('Тарифф не оплачен, пополните баланс для активации тарифа')
          if (json?.required_topup) {
            setAddedSum(json?.required_topup)
            setIsModalBalance(true)
          }
        } else {
          addSuccessPush('Тариф активирован')
        }
        setTimeout(() => fetchGetModelsTariff(1), 2000)
      })
      .catch(handleRequestError)
      .finally(() => {
        setTimeout(() => fetchGetActivateTariff(), 1000)
        setTimeout(() => fetchGetActivateTariff(), 10000)
      });
  };

  useEffect(() => {
    fetchGetModelsTariff(1)
  }, []);

  return <section className="tariff-plan">
    <div className="tariff-plan__inner">
      {child}

      <div className="tariff-plan__grid">
        {loading ?
          <div className="tariff-plan__loader">
            {[...Array(4)].map((_el, ind) => {
              return <Sceleton
                width={'250px'}
                height={'467px'}
              />
            })}
          </div>
          :
          parseTariffResponse(modelsTariff).map((tariff) => (
            <TariffCard
              key={tariff.id}
              tariff={tariff}
              onCtaClick={fetchActivateTariff} />
          ))}
      </div>
    </div>
  </section>
}

export default React.memo(TariffPlanV2)